import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useGetPromotionsQuery } from 'store/api/strapiService/promotionsController';
import { viewModeSelector } from 'store/slices/viewMode';

import { findPathPages } from 'utils/pages';
import { getDateFilters, getSliderData, sortByMain } from 'utils/promotions';
import { VIEW_MODE } from 'utils/viewMode';

import { Skeleton } from '@mui/material';

import AnimationLayout from 'components/Layouts/Animation/AnimationLayout';
import HomeComponentsLayout from 'components/Layouts/HomeComponents/HomeComponentsLayout';
import Slider from 'components/UI/Slider/Slider';

import { PAGE_PATH } from 'components/config/pages';
import { CATEGORIES } from 'components/config/promotions';
import { PROVIDERS } from 'config/organization';

import styles from './UpcomingPromotions.module.scss';

const itemsSizes = {
  [VIEW_MODE.SIMPLE]: 3,
  [VIEW_MODE.WIDE]: 4,
};

const date = new Date();

const UpcomingPromotions = () => {
  const viewMode = useSelector(viewModeSelector.getViewMode);

  const { data: runningData, isLoading: runningLoading } =
    useGetPromotionsQuery({
      provider: PROVIDERS.evoplay,
      dateFilters: getDateFilters(CATEGORIES.running.value, date),
      sortByMain: true,
      limit: itemsSizes[viewMode],
    });

  const { data: comingSoonData, isLoading: comingSoonLoading } =
    useGetPromotionsQuery({
      provider: PROVIDERS.evoplay,
      dateFilters: getDateFilters(CATEGORIES.comingSoon.value, date),
      sortByMain: true,
      limit: itemsSizes[viewMode],
    });

  const data = useMemo(() => {
    const getItems = (data) => data?.data ?? [];

    return sortByMain([...getItems(runningData), ...getItems(comingSoonData)]);
  }, [runningData, comingSoonData]);

  const sliderData = useMemo(
    () => getSliderData(data.slice(0, itemsSizes[viewMode])),
    [data, viewMode],
  );

  const isLoading = useMemo(
    () => [runningLoading, comingSoonLoading].some(Boolean),
    [runningLoading, comingSoonLoading],
  );

  if (!isLoading && !sliderData?.length) return null;

  return (
    <HomeComponentsLayout
      title="Active and Upcoming Promotions"
      linkPath={findPathPages(PAGE_PATH.PROMOTIONS).replace(
        ':provider',
        PROVIDERS.evoplay,
      )}
      linkTitle="All Evoplay Promotions"
      styleWrapper={styles.wrapper}
      styleHeader={styles.header}
    >
      {!isLoading ? (
        <AnimationLayout animation="fadeInUp">
          <div className={styles.content}>
            <Slider data={sliderData} type="horizontal" />
          </div>
        </AnimationLayout>
      ) : (
        <div className={styles.content}>
          <div className={styles.skeletons}>
            <Skeleton variant="rounded" height={407} />
            <div className={styles.skeletonsItems}>
              {Array(itemsSizes[viewMode])
                .fill(null)
                .map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rounded"
                    width="100%"
                    height={110}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </HomeComponentsLayout>
  );
};

export default UpcomingPromotions;
